
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import useAdminStatus from '@/hooks/useAdminStatus';
import { supabase } from "@/integrations/supabase/client";
import { getMFAStatus } from "@/services/user/mfaService";
import { toast } from "@/hooks/use-toast";

export const ProtectedRoute = ({ children, adminOnly = false }: { children: JSX.Element, adminOnly?: boolean }) => {
  const location = useLocation();
  const { isAdmin, isLoading: adminLoading } = useAdminStatus();
  const [user, setUser] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [requires2FA, setRequires2FA] = React.useState(false);
  
  React.useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get the current session
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Auth error:", error);
          setUser(null);
          setIsLoading(false);
          return;
        }
        
        const currentUser = data.session?.user || null;
        setUser(currentUser);
        
        if (currentUser) {
          // Check if this is an admin route and if 2FA is needed
          if (adminOnly) {
            try {
              // Check if user needs 2FA
              const mfaStatus = await getMFAStatus();
              console.log("MFA status:", mfaStatus);
              
              // If 2FA is required but not completed, redirect to dashboard to complete setup
              if (mfaStatus.requires2FA && !mfaStatus.completed2FA) {
                setRequires2FA(true);
              }
            } catch (mfaErr) {
              console.error("Error checking MFA status:", mfaErr);
            }
          }
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error("Auth check error:", err);
        setIsLoading(false);
      }
    };

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    checkAuth();
    
    return () => subscription.unsubscribe();
  }, [adminOnly]);
  
  if (isLoading || (adminOnly && adminLoading)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md space-y-4 p-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }
  
  if (!user) {
    // If user is not authenticated, redirect to login page
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }
  
  // If 2FA is required but not completed, redirect to dashboard for setup
  if (requires2FA && location.pathname !== '/dashboard') {
    toast({
      title: "2FA Required",
      description: "Please complete two-factor authentication setup before accessing admin areas.",
      variant: "destructive"
    });
    return <Navigate to="/dashboard" replace />;
  }
  
  // If this is an admin route and user is not an admin
  if (adminOnly && !isAdmin) {
    toast({
      title: "Access Denied",
      description: "You don't have permission to access this page.",
      variant: "destructive"
    });
    return <Navigate to="/unauthorized" replace />;
  }
  
  // If user is authenticated, allow access to the protected route
  return children;
};
